import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Search } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  smallTextSize,
  largeTextSize,
  borderPrimary,
  whiteBG,
} from '../../../styling/stylingHelper';
import { useWasteStore } from '../../../stores/appWasteStore';
import { WasteEntry, CreateWasteRequest, UpdateWasteRequest } from '../../../services/waste/wasteService';
import { fetchIngredientSuggestions } from '../../../services/waste/wasteService';
import { fetchRevenueItemNames } from '../../../services/costPrice/costPriceService';

interface LogWasteModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
  existingEntry?: WasteEntry;
  restaurantId: string;
}

type DisplayUnit = 'g' | 'kg' | 'ml' | 'L' | 'pieces';

const DISPLAY_UNITS: { value: DisplayUnit; label: string }[] = [
  { value: 'g', label: 'g' },
  { value: 'kg', label: 'kg' },
  { value: 'ml', label: 'ml' },
  { value: 'L', label: 'L' },
  { value: 'pieces', label: 'pcs' },
];

const REASON_OPTIONS = [
  { value: 'expired', labelKey: 'waste.reasonExpired' },
  { value: 'over_prep', labelKey: 'waste.reasonOverPrep' },
  { value: 'mistake', labelKey: 'waste.reasonMistake' },
  { value: 'bad_quality', labelKey: 'waste.reasonBadQuality' },
  { value: 'accident', labelKey: 'waste.reasonAccident' },
  { value: 'other', labelKey: 'waste.reasonOther' },
];

function toBaseUnit(quantity: number, displayUnit: DisplayUnit): { quantity: number; unit: 'g' | 'ml' | 'p' } {
  switch (displayUnit) {
    case 'kg': return { quantity: quantity * 1000, unit: 'g' };
    case 'L': return { quantity: quantity * 1000, unit: 'ml' };
    case 'g': return { quantity, unit: 'g' };
    case 'ml': return { quantity, unit: 'ml' };
    case 'pieces': return { quantity, unit: 'p' };
  }
}

function fromBaseUnit(quantity: number, unit: 'g' | 'ml' | 'p'): { quantity: number; displayUnit: DisplayUnit } {
  if (unit === 'g' && quantity >= 1000) return { quantity: quantity / 1000, displayUnit: 'kg' };
  if (unit === 'ml' && quantity >= 1000) return { quantity: quantity / 1000, displayUnit: 'L' };
  if (unit === 'p') return { quantity, displayUnit: 'pieces' };
  if (unit === 'g') return { quantity, displayUnit: 'g' };
  return { quantity, displayUnit: 'ml' };
}

export default function LogWasteModal({
  isOpen,
  onClose,
  mode,
  existingEntry,
  restaurantId,
}: LogWasteModalProps) {
  const { t } = useTranslation();
  const wasteStore = useWasteStore();

  const [wasteType, setWasteType] = useState<'dish' | 'ingredient'>('ingredient');
  const [itemName, setItemName] = useState('');
  const [quantityStr, setQuantityStr] = useState('');
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>('g');
  const [reason, setReason] = useState<string | null>(null);
  const [reasonNote, setReasonNote] = useState('');
  const [wasteDate, setWasteDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dish search state
  const [dishSearchQuery, setDishSearchQuery] = useState('');
  const [revenueItems, setRevenueItems] = useState<string[]>([]);

  // Ingredient suggestions
  const [ingredientSuggestions, setIngredientSuggestions] = useState<string[]>([]);

  // Load revenue items and ingredient suggestions
  useEffect(() => {
    if (!isOpen) return;
    fetchRevenueItemNames(restaurantId).then(setRevenueItems).catch(console.error);
    fetchIngredientSuggestions(restaurantId).then(setIngredientSuggestions).catch(console.error);
  }, [isOpen, restaurantId]);

  // Init form from existing entry or defaults
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && existingEntry) {
      setWasteType(existingEntry.wasteType);
      setItemName(existingEntry.itemName);
      if (existingEntry.wasteType === 'dish') {
        setQuantityStr(String(existingEntry.quantity));
        setDisplayUnit('pieces');
      } else {
        const converted = fromBaseUnit(existingEntry.quantity, existingEntry.unit);
        setQuantityStr(String(converted.quantity));
        setDisplayUnit(converted.displayUnit);
      }
      setReason(existingEntry.reason);
      setReasonNote(existingEntry.reasonNote || '');
      // Format date for datetime-local input
      const d = new Date(existingEntry.wasteDate);
      setWasteDate(toLocalDatetimeString(d));
      setDishSearchQuery(existingEntry.wasteType === 'dish' ? existingEntry.itemName : '');
    } else {
      setWasteType('ingredient');
      setItemName('');
      setQuantityStr('');
      setDisplayUnit('g');
      setReason(null);
      setReasonNote('');
      setWasteDate(toLocalDatetimeString(new Date()));
      setDishSearchQuery('');
    }
    setError(null);
    setSaving(false);
  }, [isOpen, mode, existingEntry]);

  function toLocalDatetimeString(d: Date): string {
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  }

  const filteredDishItems = useMemo(() => {
    if (!dishSearchQuery.trim()) return revenueItems.slice(0, 20);
    const q = dishSearchQuery.toLowerCase();
    return revenueItems.filter(item => item.toLowerCase().includes(q)).slice(0, 20);
  }, [dishSearchQuery, revenueItems]);

  const handleWasteTypeChange = (type: 'dish' | 'ingredient') => {
    setWasteType(type);
    setItemName('');
    setDishSearchQuery('');
    if (type === 'dish') {
      setDisplayUnit('pieces');
    } else {
      setDisplayUnit('g');
    }
  };

  const handleReasonToggle = (value: string) => {
    if (reason === value) {
      setReason(null);
      setReasonNote('');
    } else {
      setReason(value);
      if (value !== 'other') setReasonNote('');
    }
  };

  const handleSave = async () => {
    // Validate
    if (!itemName.trim()) {
      setError(t('waste.itemName') + ' is required');
      return;
    }
    const qty = parseFloat(quantityStr);
    if (!qty || qty <= 0) {
      setError(t('waste.quantity') + ' must be greater than 0');
      return;
    }

    setError(null);
    setSaving(true);

    const base = wasteType === 'dish'
      ? { quantity: qty, unit: 'p' as const }
      : toBaseUnit(qty, displayUnit);

    try {
      if (mode === 'create') {
        const request: CreateWasteRequest = {
          wasteDate: new Date(wasteDate).toISOString(),
          wasteType,
          itemName: itemName.trim(),
          quantity: Math.round(base.quantity * 100) / 100,
          unit: base.unit,
          reason: reason || undefined,
          reasonNote: reason === 'other' ? reasonNote || undefined : undefined,
        };
        await wasteStore.create(restaurantId, request);
      } else if (existingEntry) {
        const request: UpdateWasteRequest = {
          wasteDate: new Date(wasteDate).toISOString(),
          wasteType,
          itemName: itemName.trim(),
          quantity: Math.round(base.quantity * 100) / 100,
          unit: base.unit,
          reason: reason || null,
          reasonNote: reason === 'other' ? reasonNote || null : null,
        };
        await wasteStore.update(existingEntry.id, request);
      }
      onClose();
    } catch (err) {
      console.error('Failed to save waste entry', err);
      setError('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4" onClick={onClose}>
      <div
        className={cn(
          "bg-white dark:bg-zinc-900 rounded-none sm:rounded-xl shadow-xl w-full max-w-lg border h-full sm:h-auto max-h-none sm:max-h-[90vh] overflow-y-auto",
          borderPrimary
        )}
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5">
          {/* Header */}
          <div className="flex justify-between items-center mb-5">
            <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>
              {mode === 'create' ? t('waste.logNewWaste') : t('waste.editWaste')}
            </h2>
            <button onClick={onClose} className="text-gray-500 dark:text-textTertiary-dark hover:text-gray-700 dark:hover:text-textSecondary-dark">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Waste Type Toggle */}
          <div className="flex gap-2 mb-4">
            {(['ingredient', 'dish'] as const).map(type => (
              <button
                key={type}
                onClick={() => mode === 'create' ? handleWasteTypeChange(type) : undefined}
                disabled={mode === 'edit'}
                className={cn(
                  "flex-1 py-2 rounded-lg text-sm font-medium transition",
                  wasteType === type
                    ? "bg-primary text-white"
                    : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-700",
                  mode === 'edit' && "opacity-60 cursor-not-allowed"
                )}
              >
                {type === 'dish' ? t('waste.typeDish') : t('waste.typeIngredient')}
              </button>
            ))}
          </div>

          {/* Item Name - Dish mode */}
          {wasteType === 'dish' && (
            <div className="mb-4">
              <label className={cn(smallTextSize, semiBoldText, secondaryText, "block mb-1")}>
                {t('waste.itemName')}
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-textQuaternary-dark" />
                <input
                  type="text"
                  value={dishSearchQuery}
                  onChange={e => {
                    setDishSearchQuery(e.target.value);
                    // Don't clear itemName here - only clear when type changes
                  }}
                  placeholder={t('waste.searchDish')}
                  className={cn(
                    "w-full pl-9 pr-3 py-2 border rounded-lg text-sm",
                    borderPrimary,
                    "bg-white dark:bg-zinc-800",
                    primaryText
                  )}
                />
              </div>
              {itemName && (
                <div className="mt-2">
                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-dark rounded-full text-sm font-medium">
                    {itemName}
                    <button onClick={() => { setItemName(''); setDishSearchQuery(''); }} className="hover:text-primary/70 dark:hover:text-primary-dark/70">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                </div>
              )}
              {!itemName && (
                <div className="mt-2 max-h-40 overflow-y-auto border rounded-lg divide-y divide-gray-100 dark:divide-zinc-800" style={{ borderColor: 'var(--border-primary)' }}>
                  {filteredDishItems.length === 0 ? (
                    <p className={cn("px-3 py-2 text-sm", secondaryText)}>No dishes found</p>
                  ) : (
                    filteredDishItems.map(item => (
                      <button
                        key={item}
                        onClick={() => {
                          setItemName(item);
                          setDishSearchQuery(item);
                        }}
                        className={cn(
                          "w-full text-left px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-zinc-800 transition",
                          primaryText
                        )}
                      >
                        {item}
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Item Name - Ingredient mode */}
          {wasteType === 'ingredient' && (
            <div className="mb-4">
              <label className={cn(smallTextSize, semiBoldText, secondaryText, "block mb-1")}>
                {t('waste.itemName')}
              </label>
              <input
                type="text"
                value={itemName}
                onChange={e => setItemName(e.target.value)}
                placeholder={t('waste.enterIngredient')}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-sm",
                  borderPrimary,
                  "bg-white dark:bg-zinc-800",
                  primaryText
                )}
              />
              {ingredientSuggestions.length > 0 && (
                <div className="mt-2">
                  <p className={cn(smallTextSize, secondaryText, "mb-1")}>{t('waste.suggestions')}</p>
                  <div className="flex flex-wrap gap-1">
                    {ingredientSuggestions.map(name => (
                      <button
                        key={name}
                        onClick={() => setItemName(name)}
                        className={cn(
                          "px-2.5 py-1 rounded-full text-xs font-medium transition",
                          itemName === name
                            ? "bg-primary text-white"
                            : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200"
                        )}
                      >
                        {name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Quantity + Unit */}
          <div className="flex gap-3 mb-4">
            <div className={wasteType === 'dish' ? 'w-full' : 'flex-1'}>
              <label className={cn(smallTextSize, semiBoldText, secondaryText, "block mb-1")}>
                {wasteType === 'dish' ? t('waste.quantity') + ' (pcs)' : t('waste.quantity')}
              </label>
              <input
                type="number"
                step={wasteType === 'dish' ? '1' : 'any'}
                min="0"
                value={quantityStr}
                onChange={e => setQuantityStr(e.target.value)}
                placeholder={wasteType === 'dish' ? '1' : '0'}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-sm",
                  borderPrimary,
                  "bg-white dark:bg-zinc-800",
                  primaryText
                )}
              />
            </div>
            {wasteType === 'ingredient' && (
              <div className="w-28">
                <label className={cn(smallTextSize, semiBoldText, secondaryText, "block mb-1")}>
                  {t('waste.unit')}
                </label>
                <select
                  value={displayUnit}
                  onChange={e => setDisplayUnit(e.target.value as DisplayUnit)}
                  className={cn(
                    "w-full px-3 py-2 border rounded-lg text-sm",
                    borderPrimary,
                    "bg-white dark:bg-zinc-800",
                    primaryText
                  )}
                >
                  {DISPLAY_UNITS.map(u => (
                    <option key={u.value} value={u.value}>{u.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Date */}
          <div className="mb-4">
            <label className={cn(smallTextSize, semiBoldText, secondaryText, "block mb-1")}>
              {t('waste.wasteDate')}
            </label>
            <input
              type="datetime-local"
              value={wasteDate}
              onChange={e => setWasteDate(e.target.value)}
              className={cn(
                "w-full px-3 py-2 border rounded-lg text-sm",
                borderPrimary,
                "bg-white dark:bg-zinc-800",
                primaryText
              )}
            />
          </div>

          {/* Reason */}
          <div className="mb-4">
            <label className={cn(smallTextSize, semiBoldText, secondaryText, "block mb-2")}>
              {t('waste.reason')}
            </label>
            <div className="flex flex-wrap gap-2">
              {REASON_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => handleReasonToggle(opt.value)}
                  className={cn(
                    "px-3 py-1.5 rounded-lg text-xs font-medium transition",
                    reason === opt.value
                      ? "bg-primary text-white"
                      : "bg-gray-100 dark:bg-zinc-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-zinc-700"
                  )}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
            {reason === 'other' && (
              <input
                type="text"
                value={reasonNote}
                onChange={e => setReasonNote(e.target.value)}
                placeholder={t('waste.reasonNote')}
                className={cn(
                  "w-full px-3 py-2 border rounded-lg text-sm mt-2",
                  borderPrimary,
                  "bg-white dark:bg-zinc-800",
                  primaryText
                )}
              />
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="text-red-500 dark:text-textRed-dark text-sm mb-3">{error}</p>
          )}

          {/* Actions */}
          <div className="-mx-5 mt-5">
            <div className="border-t border-borderPrimary dark:border-borderPrimary-dark w-full" />
            <div className="flex justify-end gap-3 pt-4 px-5">
              <button
                onClick={onClose}
                className={cn(
                  smallTextSize,
                  secondaryText,
                  semiBoldText,
                  "rounded-xl px-4 py-3",
                  whiteBG,
                  "border",
                  borderPrimary,
                  "hover:bg-gray-50 dark:hover:bg-white/5",
                  "flex-1"
                )}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className={cn(
                  smallTextSize,
                  "text-white dark:text-white",
                  semiBoldText,
                  "bg-primary dark:bg-primary rounded-xl px-4 py-3",
                  "hover:bg-primary/90",
                  "flex-1",
                  saving && "opacity-50 cursor-not-allowed"
                )}
              >
                {saving ? '...' : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
